/**
 * ApplicationsTable — Interactive, sortable table view for HR/Admin.
 *
 * Features
 *   • Click any column header to sort (asc → desc → reset)
 *   • Inline status dropdown — updates immediately via API
 *   • Click row to open full detail panel (uses parent's onSelect)
 *   • Horizontal scroll on mobile; sticky first column
 *   • Color-coded status pills
 */
import React, { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, Mail, Phone, Loader2 } from 'lucide-react';

const STATUS_TONE = {
  Submitted:        'bg-blue-50 text-blue-700 border-blue-200',
  'In Review':      'bg-amber-50 text-amber-700 border-amber-200',
  Interview:        'bg-purple-50 text-purple-700 border-purple-200',
  'Offer Extended': 'bg-orange-50 text-orange-700 border-orange-200',
  'Offer Accepted': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected:         'bg-red-50 text-red-700 border-red-200',
};
const STATUS_OPTIONS = [
  'Submitted', 'In Review', 'Interview',
  'Offer Extended', 'Offer Accepted', 'Rejected',
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: '2-digit',
    });
  } catch { return String(iso).slice(0, 10); }
};

const COLUMNS = [
  { key: 'applicant_name', label: 'Candidate' },
  { key: 'designation', label: 'Role' },
  { key: 'department_name', label: 'Dept' },
  { key: 'applicant_email', label: 'Email' },
  { key: 'applicant_phone', label: 'Phone' },
  { key: 'expected_ctc', label: 'Expected CTC' },
  { key: 'years_of_experience', label: 'Exp (yrs)' },
  { key: 'status', label: 'Status' },
  { key: 'created_at', label: 'Applied' },
];

const HeaderCell = ({ col, sort, onClick }) => {
  const active = sort.key === col.key;
  const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      onClick={() => onClick(col.key)}
      data-testid={`app-table-sort-${col.key}`}
      className={`px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium border-b border-neutral-100 cursor-pointer select-none whitespace-nowrap transition-colors ${
        active ? 'text-pink-700 bg-pink-50/40' : 'text-neutral-500 hover:bg-neutral-50'
      }`}
    >
      <div className="inline-flex items-center gap-1">
        {col.label}
        <Icon className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </div>
    </th>
  );
};

const ApplicationsTable = ({
  applications = [],
  onSelect,
  onStatusChange,
}) => {
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [busyId, setBusyId] = useState(null);

  const handleHeaderClick = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: 'created_at', dir: 'desc' };
    });
  };

  const sorted = useMemo(() => {
    const rows = [...applications];
    rows.sort((a, b) => {
      const k = sort.key;
      let av = a?.[k]; let bv = b?.[k];
      if (av === undefined || av === null) av = '';
      if (bv === undefined || bv === null) bv = '';
      if (['expected_ctc', 'years_of_experience'].includes(k)) {
        av = Number(av) || 0; bv = Number(bv) || 0;
      } else if (k === 'created_at') {
        av = new Date(av).getTime() || 0; bv = new Date(bv).getTime() || 0;
      } else {
        av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
      }
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [applications, sort]);

  const onStatusSelect = async (app, newStatus, e) => {
    e.stopPropagation();
    if (newStatus === app.status) return;
    setBusyId(app._id);
    try {
      await onStatusChange?.(app, newStatus);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden" data-testid="applications-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              {COLUMNS.map((c) => <HeaderCell key={c.key} col={c} sort={sort} onClick={handleHeaderClick} />)}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="text-center py-10 text-sm text-neutral-500">
                  No applications match the current filters
                </td>
              </tr>
            ) : sorted.map((app) => (
              <tr
                key={app._id}
                onClick={() => onSelect?.(app)}
                data-testid={`app-row-${app._id}`}
                className="border-b border-neutral-50 hover:bg-pink-50/30 cursor-pointer transition-colors"
              >
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <div className="font-medium text-[#1a1a2e]">{app.applicant_name || '—'}</div>
                  {app.location && <div className="text-[10px] text-neutral-400">{app.location}</div>}
                </td>
                <td className="px-3 py-2.5 text-neutral-700 max-w-[200px] truncate" title={app.designation}>
                  {app.designation || '—'}
                </td>
                <td className="px-3 py-2.5 text-neutral-600">{app.department_name || '—'}</td>
                <td className="px-3 py-2.5 text-neutral-600">
                  {app.applicant_email ? (
                    <a
                      href={`mailto:${app.applicant_email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 hover:text-pink-700"
                    >
                      <Mail className="w-3 h-3" />
                      <span className="max-w-[180px] truncate">{app.applicant_email}</span>
                    </a>
                  ) : '—'}
                </td>
                <td className="px-3 py-2.5 text-neutral-600">
                  {app.applicant_phone ? (
                    <a
                      href={`tel:${app.applicant_phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 hover:text-pink-700"
                    >
                      <Phone className="w-3 h-3" />
                      {app.applicant_phone}
                    </a>
                  ) : '—'}
                </td>
                <td className="px-3 py-2.5 text-neutral-700 tabular-nums">
                  {(() => {
                    const n = Number(app.expected_ctc);
                    return app.expected_ctc && isFinite(n) ? `₹${n.toLocaleString('en-IN')}` : '—';
                  })()}
                </td>
                <td className="px-3 py-2.5 text-neutral-700 tabular-nums">{(app.years_of_experience !== '' && app.years_of_experience != null) ? app.years_of_experience : '—'}</td>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center gap-1.5">
                    <select
                      value={app.status || 'Submitted'}
                      onChange={(e) => onStatusSelect(app, e.target.value, e)}
                      disabled={busyId === app._id}
                      data-testid={`app-status-select-${app._id}`}
                      className={`text-[11px] font-medium px-2 py-1 rounded-md border focus:outline-none focus:ring-2 focus:ring-pink-200 ${
                        STATUS_TONE[app.status] || 'bg-neutral-50 text-neutral-700 border-neutral-200'
                      }`}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {busyId === app._id && <Loader2 className="w-3 h-3 animate-spin text-neutral-400" />}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-neutral-500 whitespace-nowrap tabular-nums">
                  {fmtDate(app.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 text-[10px] text-neutral-400 border-t border-neutral-100 bg-neutral-50">
        Click a row to open full details · Click a column header to sort · Change status from the dropdown
      </div>
    </div>
  );
};

export default ApplicationsTable;
